import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, unref as _unref, Transition as _Transition, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
  key: 0,
  "data-cy": "custom-field-edit-panel",
  class: "fixed top-20 right-0 bg-white shadow-2xl z-50 flex flex-col rounded-lg overflow-hidden",
  style: {
    "width": "40vw",
    "min-width": "500px",
    "max-width": "800px",
    "height": "calc(100vh - 80px)"
  }
};
const _hoisted_2 = {
  class: "flex items-center justify-between px-6 py-4 border-b border-gray-200"
};
const _hoisted_3 = {
  class: "text-xl font-semibold text-gray-900"
};
const _hoisted_4 = {
  key: 0,
  class: "flex-1 overflow-auto p-6"
};
const _hoisted_5 = {
  key: 1,
  class: "flex-1 overflow-auto p-6"
};
const _hoisted_6 = {
  class: "mb-6"
};
const _hoisted_7 = {
  key: 0,
  class: "grid grid-cols-2 gap-4 mb-4"
};
const _hoisted_8 = {
  key: 1,
  class: "grid grid-cols-2 gap-4 mb-4"
};
const _hoisted_9 = {
  key: 2,
  class: "flex items-center mb-4"
};
const _hoisted_10 = {
  key: 0,
  class: "mb-6"
};
const _hoisted_11 = {
  class: "flex items-center justify-between mb-4"
};
const _hoisted_12 = {
  key: 0,
  class: "text-sm text-error italic"
};
const _hoisted_13 = {
  key: 1,
  class: "space-y-2"
};
const _hoisted_14 = {
  class: "flex items-center mb-4"
};
const _hoisted_15 = {
  key: 1,
  class: "mb-6 p-4 bg-blue-50 rounded"
};
const _hoisted_16 = {
  class: "flex items-start gap-2"
};
const _hoisted_17 = {
  class: "text-sm text-blue-800 mt-1"
};
const _hoisted_18 = {
  key: 0
};
const _hoisted_19 = {
  key: 1
};
const _hoisted_20 = {
  key: 2
};
const _hoisted_21 = {
  key: 3
};
const _hoisted_22 = {
  class: "flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50",
  style: {
    "margin-right": "56px"
  }
};
import { ref, computed, watch, reactive, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useCustomFieldsStore } from './useCustomFieldsStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSwitch from '@/components/LlSwitch.vue';
export default {
  __name: 'CustomFieldEditPanel',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    customFieldId: {
      type: [String, Number],
      default: null
    },
    state: {
      type: Object,
      required: false
    },
    alerts: {
      type: Object,
      required: false
    }
  },
  emits: ['update:modelValue', 'close', 'saved'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const customFieldsStore = useCustomFieldsStore();
    const {
      saving
    } = storeToRefs(customFieldsStore);
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();
    const formRef = ref(null);
    const formValid = ref(false);
    const loading = ref(false);
    const usage = ref(null);
    const originalFormSnapshot = ref(null);

    // Form state
    const formState = reactive({
      name: '',
      entityType: null,
      fieldType: null,
      active: true,
      showOnInvoice: false,
      options: [] // For dropdown type
    });
    const takeSnapshot = () => ({
      name: formState.name,
      active: formState.active,
      showOnInvoice: formState.showOnInvoice,
      options: formState.options.map(o => ({
        id: o.id,
        label: o.label
      }))
    });
    const hasChanges = computed(() => {
      if (!isEditMode.value || !originalFormSnapshot.value) return true;
      const snap = originalFormSnapshot.value;
      if (formState.name !== snap.name) return true;
      if (formState.active !== snap.active) return true;
      if (formState.showOnInvoice !== snap.showOnInvoice) return true;
      if (formState.options.length !== snap.options.length) return true;
      for (let i = 0; i < formState.options.length; i++) {
        if (formState.options[i].label !== snap.options[i].label) return true;
      }
      return false;
    });

    // Options
    const entityTypeOptions = [{
      label: 'Client',
      id: 'client'
    }, {
      label: 'Matter',
      id: 'matter'
    }, {
      label: 'User',
      id: 'account'
    }];
    const fieldTypeOptions = [{
      label: 'Text',
      id: 'text'
    }, {
      label: 'Number',
      id: 'number'
    }, {
      label: 'Dropdown',
      id: 'dropdown'
    }];

    // Computed
    const isEditMode = computed(() => !!props.customFieldId);

    // Computed property to determine if save button should be enabled
    const isSaveEnabled = computed(() => {
      // Name is required
      if (!formState.name || formState.name.trim() === '') {
        return false;
      }

      // Entity type is required (only in create mode, disabled in edit)
      if (!isEditMode.value && !formState.entityType) {
        return false;
      }

      // Field type is required (only in create mode, disabled in edit)
      if (!isEditMode.value && !formState.fieldType) {
        return false;
      }

      // In edit mode, require actual changes
      if (isEditMode.value && !hasChanges.value) {
        return false;
      }

      // Dropdown fields require at least one non-empty option
      if (formState.fieldType === 'dropdown' || formState.fieldType === 'enum') {
        const validOptions = formState.options.filter(o => o.label && o.label.trim() !== '');
        if (validOptions.length === 0) {
          return false;
        }
      }
      return true;
    });

    // Validation rules
    const rules = {
      required: value => !!value || 'This field is required'
    };

    // Helper functions
    const formatEntityType = type => {
      const types = {
        client: 'Client',
        matter: 'Matter',
        user: 'User'
      };
      return types[type] || type;
    };
    const formatFieldType = type => {
      const types = {
        text: 'Text',
        'text-area': 'Text Area',
        number: 'Number',
        enum: 'Dropdown',
        dropdown: 'Dropdown'
      };
      return types[type] || type;
    };
    let optionTempIdCounter = 0;

    // Handle adding dropdown option
    const handleAddOption = () => {
      formState.options.push({
        tempId: `temp-${Date.now()}-${optionTempIdCounter++}`,
        label: ''
      });
    };

    // Handle removing dropdown option
    const handleRemoveOption = index => {
      formState.options.splice(index, 1);
    };

    // Reset form
    const resetForm = () => {
      formState.name = '';
      formState.entityType = null;
      formState.fieldType = null;
      formState.active = true;
      formState.showOnInvoice = false;
      formState.options = [];
      usage.value = null;
      if (formRef.value) {
        formRef.value.reset();
      }
    };

    // Load custom field data (edit mode)
    const loadCustomField = async () => {
      if (!props.customFieldId) {
        resetForm();
        return;
      }
      loading.value = true;
      try {
        const [customField, usageResponse] = await Promise.all([customFieldsStore.getCustomField(props.customFieldId), customFieldsStore.getCustomFieldUsage(props.customFieldId)]);
        formState.name = customField.name;

        // Determine entity type from boolean flags
        if (customField.showOnClient) formState.entityType = 'client';else if (customField.showOnMatter) formState.entityType = 'matter';else if (customField.showOnAccount) formState.entityType = 'account';else if (customField.showOnCharge) formState.entityType = 'charge';

        // Map field type (API returns 'enum' for dropdown, 'text', etc.)
        const typeMapping = {
          enum: 'dropdown',
          text: 'text',
          'text-area': 'text',
          number: 'number'
        };
        formState.fieldType = typeMapping[customField.type] || customField.type;
        formState.active = customField.active !== false; // Default to true if undefined
        formState.showOnInvoice = customField.showOnInvoice || false;

        // Load dropdown options if enum/dropdown type
        formState.options = customField.enumOptions ? customField.enumOptions.map(opt => ({
          id: opt.id,
          label: opt.value || opt.label || opt
        })) : [];
        usage.value = usageResponse;
        await nextTick();
        originalFormSnapshot.value = takeSnapshot();
      } catch (error) {
        handleError(error);
      } finally {
        loading.value = false;
      }
    };

    // Watch for changes in customFieldId
    watch(() => props.customFieldId, () => {
      if (props.modelValue) {
        loadCustomField();
      }
    }, {
      immediate: true
    });

    // Clear showOnInvoice when entity type is user
    watch(() => formState.entityType, newType => {
      if (newType === 'account') {
        formState.showOnInvoice = false;
      }
    });

    // Watch for panel opening
    watch(() => props.modelValue, newValue => {
      if (newValue) {
        loadCustomField();
      } else {
        resetForm();
      }
    });

    // Handle close
    const handleClose = () => {
      emit('update:modelValue', false);
      emit('close');
      nextTick(() => {
        resetForm();
      });
    };

    // Handle save
    const handleSave = async () => {
      // Validate form
      const {
        valid
      } = await formRef.value.validate();
      if (!valid) {
        return;
      }

      // Validate dropdown options if dropdown type
      if (formState.fieldType === 'dropdown') {
        if (formState.options.length === 0) {
          handleError('Dropdown fields must have at least one option');
          return;
        }

        // Check for empty option labels
        const emptyOptions = formState.options.filter(opt => !opt.label || opt.label.trim() === '');
        if (emptyOptions.length > 0) {
          handleError('All dropdown options must have a label');
          return;
        }
      }

      // Prepare data - convert to API format
      const data = {
        name: formState.name,
        active: formState.active,
        showOnInvoice: formState.showOnInvoice
      };

      // Set entity type flags
      data.entity = formState.entityType;

      // Set field type (API expects 'enum' for dropdown)
      const typeMapping = {
        dropdown: 'enum',
        text: 'text',
        number: 'number'
      };
      data.type = typeMapping[formState.fieldType] || formState.fieldType;

      // Add options for dropdown (API expects enumOptions)
      if (formState.fieldType === 'dropdown') {
        data.enumOptions = formState.options.map(opt => ({
          id: opt.id,
          // Include ID if editing existing option
          label: opt.label
        }));
      }
      try {
        if (isEditMode.value) {
          await customFieldsStore.updateCustomField(props.customFieldId, data);
          handleSuccess('Custom field updated successfully');
        } else {
          await customFieldsStore.createCustomField(data);
          handleSuccess('Custom field created successfully');
        }
        emit('saved');
        handleClose();
      } catch (error) {
        handleError(error);
      }
    };
    return (_ctx, _cache) => {
      const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
      const _component_v_chip = _resolveComponent("v-chip");
      const _component_v_icon = _resolveComponent("v-icon");
      const _component_v_form = _resolveComponent("v-form");
      return _openBlock(), _createBlock(_Transition, {
        name: "slide-panel",
        "enter-active-class": "slide-panel-enter-active",
        "leave-active-class": "slide-panel-leave-active",
        "enter-from-class": "slide-panel-enter-from",
        "enter-to-class": "slide-panel-enter-to",
        "leave-from-class": "slide-panel-leave-from",
        "leave-to-class": "slide-panel-leave-to"
      }, {
        default: _withCtx(() => [__props.modelValue ? (_openBlock(), _createElementBlock("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, [_createElementVNode("h2", _hoisted_3, _toDisplayString(isEditMode.value ? 'Edit Custom Field' : 'Create Custom Field'), 1), _createVNode(LlSecondaryButton, {
          icon: "mdi-close",
          variant: "text",
          size: "small",
          "aria-label": "Close",
          "data-cy": "close-panel-btn",
          onClick: handleClose
        })]), loading.value ? (_openBlock(), _createElementBlock("div", _hoisted_4, [_createVNode(_component_v_skeleton_loader, {
          type: "article, article"
        })])) : (_openBlock(), _createElementBlock("div", _hoisted_5, [_createVNode(_component_v_form, {
          ref_key: "formRef",
          ref: formRef,
          modelValue: formValid.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => formValid.value = $event)
        }, {
          default: _withCtx(() => [_createElementVNode("div", _hoisted_6, [_cache[8] || (_cache[8] = _createElementVNode("h3", {
            class: "text-base font-semibold text-gray-900 mb-4"
          }, " Basic Information ", -1)), _createVNode(LlTextField, {
            modelValue: formState.name,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => formState.name = $event),
            label: "Field Name",
            placeholder: "e.g., Practice Area, Client Type",
            rules: [rules.required],
            required: "",
            class: "mb-4",
            "data-cy": "field-name-input"
          }, null, 8, ["modelValue", "rules"]), isEditMode.value ? (_openBlock(), _createElementBlock("div", _hoisted_7, [_createElementVNode("div", null, [_cache[6] || (_cache[6] = _createElementVNode("label", {
            class: "text-sm font-medium text-gray-700 mb-1 block"
          }, "Associated With", -1)), _createVNode(_component_v_chip, {
            size: "small",
            variant: "flat",
            class: "bg-gray-100 text-gray-900",
            "data-cy": "entity-type-pill"
          }, {
            default: _withCtx(() => [_createTextVNode(_toDisplayString(formatEntityType(formState.entityType)), 1)]),
            _: 1
          })]), _createElementVNode("div", null, [_cache[7] || (_cache[7] = _createElementVNode("label", {
            class: "text-sm font-medium text-gray-700 mb-1 block"
          }, "Field Type", -1)), _createVNode(_component_v_chip, {
            size: "small",
            variant: "flat",
            class: "bg-gray-100 text-gray-900",
            "data-cy": "field-type-pill"
          }, {
            default: _withCtx(() => [_createTextVNode(_toDisplayString(formatFieldType(formState.fieldType)), 1)]),
            _: 1
          })])])) : (_openBlock(), _createElementBlock("div", _hoisted_8, [_createVNode(LlSelect, {
            modelValue: formState.entityType,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => formState.entityType = $event),
            items: entityTypeOptions,
            label: "Associated With",
            placeholder: "Select entity type",
            rules: [rules.required],
            "return-object": false,
            required: "",
            "data-cy": "entity-type-select"
          }, null, 8, ["modelValue", "rules"]), _createVNode(LlSelect, {
            modelValue: formState.fieldType,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => formState.fieldType = $event),
            items: fieldTypeOptions,
            label: "Field Type",
            placeholder: "Select field type",
            rules: [rules.required],
            "return-object": false,
            required: "",
            "data-cy": "field-type-select"
          }, null, 8, ["modelValue", "rules"])])), formState.entityType === 'client' || formState.entityType === 'matter' ? (_openBlock(), _createElementBlock("div", _hoisted_9, [_createVNode(LlCheckbox, {
            modelValue: formState.showOnInvoice,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => formState.showOnInvoice = $event),
            label: "Include on Invoice",
            hint: "Show this field on generated invoices",
            "data-cy": "include-on-invoice-checkbox"
          }, null, 8, ["modelValue"])])) : _createCommentVNode("", true)]), formState.fieldType === 'dropdown' ? (_openBlock(), _createElementBlock("div", _hoisted_10, [_createElementVNode("div", _hoisted_11, [_cache[10] || (_cache[10] = _createElementVNode("h3", {
            class: "text-base font-semibold text-gray-900"
          }, " Dropdown Options ", -1)), _createVNode(LlSecondaryButton, {
            size: "small",
            "prepend-icon": "mdi-plus",
            onClick: handleAddOption,
            "data-cy": "add-dropdown-option-btn"
          }, {
            default: _withCtx(() => [...(_cache[9] || (_cache[9] = [_createTextVNode(" Add Option ", -1)]))]),
            _: 1
          })]), formState.options.length === 0 ? (_openBlock(), _createElementBlock("div", _hoisted_12, " At least one option is required. Click \"Add Option\" to create dropdown choices. ")) : (_openBlock(), _createElementBlock("div", _hoisted_13, [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formState.options, (option, index) => {
            return _openBlock(), _createElementBlock("div", {
              key: option.tempId || option.id,
              class: "flex items-center gap-2 p-3 bg-gray-50 rounded"
            }, [_createVNode(LlTextField, {
              modelValue: option.label,
              "onUpdate:modelValue": $event => option.label = $event,
              placeholder: "Option label",
              "hide-details": "",
              class: "flex-1",
              "data-cy": `dropdown-option-${index}`
            }, null, 8, ["modelValue", "onUpdate:modelValue", "data-cy"]), _createVNode(LlSecondaryButton, {
              icon: "mdi-delete",
              color: "error",
              size: "small",
              variant: "text",
              "aria-label": `Remove option ${index + 1}`,
              onClick: $event => handleRemoveOption(index),
              "data-cy": `remove-option-${index}`
            }, null, 8, ["aria-label", "onClick", "data-cy"])]);
          }), 128))]))])) : _createCommentVNode("", true), _createElementVNode("div", _hoisted_14, [_createVNode(LlSwitch, {
            modelValue: formState.active,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => formState.active = $event),
            label: "Active",
            hint: "Inactive fields are hidden from forms but retain their data",
            "data-cy": "active-switch"
          }, null, 8, ["modelValue"])]), isEditMode.value && usage.value && usage.value.isInUse ? (_openBlock(), _createElementBlock("div", _hoisted_15, [_createElementVNode("div", _hoisted_16, [_createVNode(_component_v_icon, {
            color: "info",
            size: "20"
          }, {
            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [_createTextVNode("mdi-information", -1)]))]),
            _: 1
          }), _createElementVNode("div", null, [_cache[15] || (_cache[15] = _createElementVNode("p", {
            class: "text-sm font-medium text-blue-900"
          }, " Usage Information ", -1)), _createElementVNode("p", _hoisted_17, [_cache[12] || (_cache[12] = _createTextVNode(" This field is currently used on ", -1)), usage.value.totalClients ? (_openBlock(), _createElementBlock("strong", _hoisted_18, _toDisplayString(usage.value.totalClients) + " client", 1)) : _createCommentVNode("", true), usage.value.totalMatters ? (_openBlock(), _createElementBlock("strong", _hoisted_19, _toDisplayString(usage.value.totalMatters) + " matter", 1)) : _createCommentVNode("", true), _cache[13] || (_cache[13] = _createTextVNode()), usage.value.totalUsers || usage.value.totalAccounts ? (_openBlock(), _createElementBlock("strong", _hoisted_20, _toDisplayString(usage.value.totalUsers || usage.value.totalAccounts) + " user", 1)) : _createCommentVNode("", true), usage.value.totalCharges ? (_openBlock(), _createElementBlock("strong", _hoisted_21, _toDisplayString(usage.value.totalCharges) + " fixed fee", 1)) : _createCommentVNode("", true), _cache[14] || (_cache[14] = _createTextVNode(" records ", -1))])])])])) : _createCommentVNode("", true)]),
          _: 1
        }, 8, ["modelValue"])])), _createElementVNode("div", _hoisted_22, [_createVNode(LlSecondaryButton, {
          onClick: handleClose,
          "data-cy": "cancel-btn"
        }, {
          default: _withCtx(() => [...(_cache[16] || (_cache[16] = [_createTextVNode(" Cancel ", -1)]))]),
          _: 1
        }), _createVNode(LlSecondaryButton, {
          color: "primary",
          loading: _unref(saving),
          disabled: !isSaveEnabled.value,
          onClick: handleSave,
          "data-cy": "save-btn"
        }, {
          default: _withCtx(() => [_createTextVNode(_toDisplayString(isEditMode.value ? 'Save Changes' : 'Create Field'), 1)]),
          _: 1
        }, 8, ["loading", "disabled"])])])) : _createCommentVNode("", true)]),
        _: 1
      });
    };
  }
};